import React, { useState, useEffect } from 'react';
import {
      CCard,
      CCardBody,
      CCardHeader,
      CCol,
      CRow,
      CButton,
      CSpinner,
      CFormInput,
      CAlert,
      CInputGroup
} from '@coreui/react';
import { toast } from 'react-toastify';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { smartThingsService } from '../../../services/smartThingsService';

const HomeAssistantIntegrations = ({ authUser }) => {
      const [isLoading, setIsLoading] = useState(false);
      const [castAvailable, setCastAvailable] = useState(false);
      const [castSession, setCastSession] = useState(null);
      const [voiceCommand, setVoiceCommand] = useState('');
      const [error, setError] = useState(null);
      const [haConfig, setHaConfig] = useState({
            url: 'http://192.168.86.155:8123',
            token: '',
      });
      const [showTokenInput, setShowTokenInput] = useState(false);
      const [homeAssistantConnected, setHomeAssistantConnected] = useState(false);

      // Initialize Cast API
      useEffect(() => {
            // Check if Cast API is already loaded
            if (window.chrome && window.chrome.cast) {
                  initCastApi();
                  return;
            }

            window['__onGCastApiAvailable'] = (isAvailable) => {
                  if (isAvailable) {
                        initCastApi();
                  }
            };

            // Load Cast API script if not already present
            if (!document.querySelector('script[src*="cast_sender.js"]')) {
                  const script = document.createElement('script');
                  script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js';
                  script.async = true;
                  document.body.appendChild(script);
            }
      }, []);

      const initCastApi = () => {
            try {
                  // Check if chrome.cast is available
                  // eslint-disable-next-line no-undef
                  if (typeof chrome === 'undefined' || !chrome.cast) {
                        console.log('Chrome Cast API not available');
                        return;
                  }
                  
                  // eslint-disable-next-line no-undef
                  const sessionRequest = new chrome.cast.SessionRequest(
                        // eslint-disable-next-line no-undef
                        chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
                  );
                  
                  // eslint-disable-next-line no-undef
                  const apiConfig = new chrome.cast.ApiConfig(
                        sessionRequest,
                        sessionListener,
                        receiverListener
                  );

                  // eslint-disable-next-line no-undef
                  chrome.cast.initialize(apiConfig, 
                        () => {
                              console.log('Cast API initialized');
                              setCastAvailable(true);
                        }, 
                        (error) => {
                              console.error('Cast API initialization error:', error);
                              setCastAvailable(false);
                        }
                  );
            } catch (error) {
                  console.error('Cast initialization error:', error);
                  setCastAvailable(false);
            }
      };

      const sessionListener = (session) => {
            console.log('New session:', session);
            setCastSession(session);
      };

      const receiverListener = (availability) => {
            // eslint-disable-next-line no-undef
            if (typeof chrome !== 'undefined' && chrome.cast && availability === chrome.cast.ReceiverAvailability.AVAILABLE) {
                  setCastAvailable(true);
            } else {
                  setCastAvailable(false);
            }
      };

      // Voice Command Functions
      const getAccessToken = async () => {
            try {
                  const auth = getAuth();
                  const provider = new GoogleAuthProvider();
                  // Add required scopes for Google Assistant
                  provider.addScope('https://www.googleapis.com/auth/assistant-sdk-prototype');
                  provider.addScope('https://www.googleapis.com/auth/cloud-platform');
                  
                  const result = await signInWithPopup(auth, provider);
                  const credential = GoogleAuthProvider.credentialFromResult(result);
                  return credential.accessToken;
            } catch (error) {
                  console.error('Auth error:', error);
                  throw error;
            }
      };

      const sendVoiceCommand = async () => {
            try {
                  setIsLoading(true);
                  
                  // Get Firebase Functions instance
                  const functions = getFunctions();
                  const sendCommand = httpsCallable(functions, 'sendGoogleCommand');

                  // Call the function
                  const result = await sendCommand({ command: voiceCommand });
                  console.log('Voice command result:', result.data);
                  
                  toast.success('Voice command sent successfully');
                  setVoiceCommand('');
            } catch (error) {
                  console.error('Voice command error:', error);
                  toast.error('Failed to send voice command');
            } finally {
                  setIsLoading(false);
            }
      };

      const connectSmartThings = () => {
            const authUrl = smartThingsService.getOAuthUrl();
            window.location.href = authUrl;
      };

      // Function to test connection with provided token
      const testHomeAssistantConnection = async () => {
            try {
                  setIsLoading(true);
                  const response = await fetch(`${haConfig.url}/api/`, {
                        method: 'GET',
                        headers: {
                              'Authorization': `Bearer ${haConfig.token}`,
                              'Content-Type': 'application/json',
                        },
                  });

                  if (response.ok) {
                        // Store token securely in localStorage
                        localStorage.setItem('ha_token', haConfig.token);
                        localStorage.setItem('ha_url', haConfig.url);
                        setHomeAssistantConnected(true);
                        setShowTokenInput(false);
                        toast.success('Successfully connected to Home Assistant');
                  } else {
                        throw new Error('Invalid token or connection failed');
                  }
            } catch (error) {
                  console.error('Home Assistant connection error:', error);
                  toast.error('Failed to connect: Invalid token or server unreachable');
                  setHomeAssistantConnected(false);
            } finally {
                  setIsLoading(false);
            }
      };

      // Load saved configuration on component mount
      useEffect(() => {
            const savedToken = localStorage.getItem('ha_token');
            const savedUrl = localStorage.getItem('ha_url');
            if (savedToken && savedUrl) {
                  setHaConfig({
                        url: savedUrl,
                        token: savedToken,
                  });
                  // Test the saved connection
                  testHomeAssistantConnection();
            }
      }, []);

      // Example of how to make an API call to Home Assistant
      const callHomeAssistant = async (endpoint, method = 'GET', data = null) => {
            try {
                  const response = await fetch(`${haConfig.url}/api/${endpoint}`, {
                        method,
                        headers: {
                              'Authorization': `Bearer ${haConfig.token}`,
                              'Content-Type': 'application/json',
                        },
                        ...(data && { body: JSON.stringify(data) }),
                  });
                  
                  if (!response.ok) throw new Error('API call failed');
                  return await response.json();
            } catch (error) {
                  console.error('Home Assistant API error:', error);
                  toast.error('Failed to communicate with Home Assistant');
                  throw error;
            }
      };

      return (
            <CRow>
                  <CCol md={12}>
                        <CCard className="mb-4">
                              <CCardHeader>Google Home Integration</CCardHeader>
                              <CCardBody>
                                    {error && (
                                          <CAlert color="danger" dismissible>
                                                {error}
                                          </CAlert>
                                    )}

                                    {/* Cast Controls */}
                                    <div className="mb-4">
                                          <h4>Cast Devices</h4>
                                          <div className="d-flex align-items-center gap-2">
                                                <CButton 
                                                      color={castAvailable ? "success" : "secondary"}
                                                      onClick={() => {
                                                            // eslint-disable-next-line no-undef
                                                            if (castAvailable && typeof chrome !== 'undefined' && chrome.cast) {
                                                                  // eslint-disable-next-line no-undef
                                                                  chrome.cast.requestSession(
                                                                        (session) => {
                                                                              setCastSession(session);
                                                                              toast.success('Connected to Cast device');
                                                                        },
                                                                        (error) => {
                                                                              console.error('Cast session error:', error);
                                                                              toast.error('Failed to connect to Cast device');
                                                                        }
                                                                  );
                                                            }
                                                      }}
                                                      disabled={!castAvailable}
                                                >
                                                      {castAvailable ? 'Cast to Device' : 'Cast Unavailable'}
                                                </CButton>
                                          </div>
                                    </div>

                                    {/* Voice Commands */}
                                    <div className="mb-4">
                                          <h4>Voice Commands</h4>
                                          <CInputGroup>
                                                <CFormInput
                                                      placeholder="Enter voice command..."
                                                      value={voiceCommand}
                                                      onChange={(e) => setVoiceCommand(e.target.value)}
                                                />
                                                <CButton 
                                                      color="primary"
                                                      onClick={sendVoiceCommand}
                                                      disabled={isLoading || !voiceCommand}
                                                >
                                                      {isLoading ? (
                                                            <CSpinner size="sm" />
                                                      ) : (
                                                            'Send Command'
                                                      )}
                                                </CButton>
                                          </CInputGroup>
                                          <small className="mb-2">
                                                Example: "Turn on living room lights" or "Set temperature to 72 degrees"
                                          </small>
                                    </div>

                                    {/* Quick Commands */}
                                    <div className="mb-4">
                                          <h4>Quick Commands</h4>
                                          <div className="d-flex gap-2 flex-wrap">
                                                {['Turn on all lights', 'Turn off all lights', 'What\'s the temperature?'].map((command) => (
                                                      <CButton 
                                                            key={command}
                                                            color="info" 
                                                            size="sm"
                                                            onClick={() => {
                                                                  setVoiceCommand(command);
                                                                  sendVoiceCommand();
                                                            }}
                                                            disabled={isLoading}
                                                      >
                                                            {command}
                                                      </CButton>
                                                ))}
                                          </div>
                                    </div>

                                    {/* SmartThings Integration */}
                                    <div className="mb-4">
                                          <h4>SmartThings Integration</h4>
                                          <CButton 
                                                      color="primary"
                                                      onClick={connectSmartThings}
                                                      disabled={isLoading}
                                                >
                                                      Connect SmartThings
                                                </CButton>
                                    </div>

                                    {/* Home Assistant Configuration */}
                                    <div className="mb-4">
                                          <h4>Home Assistant Connection</h4>
                                          {!homeAssistantConnected && !showTokenInput && (
                                                <CButton
                                                      color="primary"
                                                      onClick={() => setShowTokenInput(true)}
                                                >
                                                      Connect to Home Assistant
                                                </CButton>
                                          )}

                                          {showTokenInput && (
                                                <div className="mt-3">
                                                      <CInputGroup className="mb-3">
                                                            <CFormInput
                                                                  placeholder="Home Assistant URL"
                                                                  value={haConfig.url}
                                                                  onChange={(e) => setHaConfig(prev => ({
                                                                        ...prev,
                                                                        url: e.target.value
                                                                  }))}
                                                            />
                                                      </CInputGroup>
                                                      <CInputGroup className="mb-3">
                                                            <CFormInput
                                                                  type="password"
                                                                  placeholder="Long-lived Access Token"
                                                                  value={haConfig.token}
                                                                  onChange={(e) => setHaConfig(prev => ({
                                                                        ...prev,
                                                                        token: e.target.value
                                                                  }))}
                                                            />
                                                      </CInputGroup>
                                                      <div className="d-flex gap-2">
                                                            <CButton
                                                                  color="success"
                                                                  onClick={testHomeAssistantConnection}
                                                                  disabled={isLoading || !haConfig.token}
                                                            >
                                                                  {isLoading ? <CSpinner size="sm" /> : 'Connect'}
                                                            </CButton>
                                                            <CButton
                                                                  color="danger"
                                                                  onClick={() => setShowTokenInput(false)}
                                                            >
                                                                  Cancel
                                                            </CButton>
                                                      </div>
                                                </div>
                                          )}

                                          {homeAssistantConnected && (
                                                <div className="mt-3">
                                                      <CAlert color="success">
                                                            Connected to Home Assistant
                                                      </CAlert>
                                                      <CButton
                                                            color="danger"
                                                            onClick={() => {
                                                                  localStorage.removeItem('ha_token');
                                                                  localStorage.removeItem('ha_url');
                                                                  setHomeAssistantConnected(false);
                                                                  setHaConfig({ url: 'http://192.168.86.155:8123', token: '' });
                                                            }}
                                                      >
                                                            Disconnect
                                                      </CButton>
                                                </div>
                                          )}
                                    </div>
                              </CCardBody>
                        </CCard>
                  </CCol>
            </CRow>
      );
};

export default HomeAssistantIntegrations;


